import {
  Alert,
  AlertTitle,
  LinearProgress,
  Snackbar,
  Typography,
} from '@mui/material';
import type {
  GridEventListener,
  GridPaginationModel,
  GridRowModel,
  GridRowModesModel,
} from '@mui/x-data-grid';
import {
  DataGrid,
  GridRowEditStopReasons,
  GridToolbarColumnsButton,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import CustomExportButton from '../../../components/CustomExportButton';
import React, { useContext, useState } from 'react';
import { toast } from 'react-toastify';
import { v4 as uuid } from 'uuid';
import FileUpload from '../../../components/fileUpload';
import { AddComponentType } from '../../../constants/addComponentType';
import { ImportType } from '../../../constants/importType';
import { TableName } from '../../../constants/table';
import TenantContext from '../../../lib/context/TenantContext';
import { useGetStockMaster } from '../../../lib/query/stockMaster';
import { uploadFileForImport } from '../../../lib/services/blob-service';
import { ValidateHeaders } from '../../../lib/services/importService';
import { sendMessage } from '../../../lib/services/queue-service';
import { updateEntity } from '../../../lib/services/api-service';
import { getCurrentDate } from '../../../lib/utils/dateHelper';
import { Columns } from './grid-columns';
import { useMsal } from '@azure/msal-react';
import { usePusher } from '../../../lib/hooks/usePusher';
import StockMappingsDialog from '../../../components/StockMappingsDialog';

const customClasses = {
  root: 'table-striped table-bordered',
  columnHeaderTitle: 'fw-bold',
  columnHeader: 'header',
  cell: 'cell',
  toolbarContainer: 'table-toolbar px-5',
  overlay: '',
  pagination: 'pagination',
  tooltip: 'tooltip',
  tooltipArrow: 'arrow',
};

const StockMaster = () => {
  const { tenant } = useContext(TenantContext);
  const { accounts } = useMsal();
  const {
    data: stockMasterData,
    isLoading,
    refetch,
  } = useGetStockMaster(tenant.id);

  const [showErrorAlert, setShowErrorAlert] = React.useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [showSuccessAlert, setShowSuccessAlert] =
    React.useState<boolean>(false);

  const [rowModesModel, setRowModesModel] = React.useState<GridRowModesModel>(
    {},
  );

  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  // State for Stock Mappings Dialog
  const [mappingsDialogOpen, setMappingsDialogOpen] = useState(false);
  const [selectedStock, setSelectedStock] = useState<{
    id: string;
    name: string;
    stockOCR: string;
    partitionKey: string;
    matchingStockAliases: string[];
  } | null>(null);

  const handleManageMappings = (
    stockId: string,
    stockName: string,
    stockOCR: string,
    partitionKey: string,
    matchingStockAliases: string[],
  ) => {
    setSelectedStock({
      id: stockId,
      name: stockName,
      stockOCR,
      partitionKey,
      matchingStockAliases,
    });
    setMappingsDialogOpen(true);
  };

  const handleCloseMappingsDialog = () => {
    setMappingsDialogOpen(false);
    setSelectedStock(null);
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer style={{ justifyContent: 'space-between' }}>
        <div>
          <GridToolbarQuickFilter />
          <GridToolbarColumnsButton />
          <GridToolbarFilterButton />
          <CustomExportButton
            fileName="stock-master"
            exportHeaders={expectedStockMasterHeaders}
          />
        </div>
      </GridToolbarContainer>
    );
  };

  const handleImportStarted = (data: any) => {
    toast.info(data.message);
    setIsProcessing(true);
  };

  const handleImportCompleted = (data: any) => {
    toast.success(data.message);
    setIsProcessing(false);
    refetch();
  };

  const handleImportFailed = (data: any) => {
    toast.error(data.message);
    setIsProcessing(false);
    refetch();
  };

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-started',
    callback: handleImportStarted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-completed',
    callback: handleImportCompleted,
  });

  usePusher({
    channelName: `user-${accounts[0]?.idTokenClaims?.preferred_username}`,
    eventName: 'import-failed',
    callback: handleImportFailed,
  });

  const showError = () => {
    setShowErrorAlert(true);
  };

  const handleRowModesModelChange = (newRowModesModel: GridRowModesModel) => {
    setRowModesModel(newRowModesModel);
  };

  const handleClose = (reason: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setShowErrorAlert(false);
  };

  const expectedStockMasterHeaders = [
    'CompanyName',
    'VendorName',
    'StockOCR',
    'StockDebitLedger',
    'UnitOfMeasurement',
    'CGSTRate',
    'SGSTRate',
    'IGSTRate',
    'GSTRate',
    'HSNSAC',
    'TaxCategory',
  ];

  const onFileUpload = (file: File, reset: () => void) => {
    ValidateHeaders(file, expectedStockMasterHeaders).then(res => {
      if (res) {
        setShowSuccessAlert(true);
        const currentBatchId = uuid();
        const filePath = `import/${
          tenant.id
        }/stock-master/${getCurrentDate()}/${currentBatchId}/${file?.name}`;
        setIsProcessing(true);
        uploadFileForImport(file, filePath)
          .then(() => {
            sendMessage(
              'import-master',
              JSON.stringify({
                fileName: filePath,
                tenantId: tenant.id,
                importType: ImportType.StockMaster,
                batchId: currentBatchId,
                userId: accounts[0]?.idTokenClaims?.preferred_username,
              }),
            );
          })
          .then(() => reset());
      } else {
        showError();
      }
    });
  };

  /* eslint no-param-reassign: ["error", { "props": false }] */
  const handleRowEditStop: GridEventListener<'rowEditStop'> = (
    params,
    event,
  ) => {
    if (params.reason === GridRowEditStopReasons.rowFocusOut) {
      event.defaultMuiPrevented = true;
    }
  };

  const processRowUpdate = async (newRow: GridRowModel) => {
    const updatedRow = { ...newRow, isNew: false, IsSystemGenerated: false };
    await updateEntity(TableName.StockMaster, {
      PartitionKey: newRow.PartitionKey,
      RowKey: newRow.RowKey,
      StockDebitLedger: newRow.StockDebitLedger,
      UnitOfMeasurement: newRow.UnitOfMeasurement,
      CGSTRate: newRow.CGSTRate,
      SGSTRate: newRow.SGSTRate,
      IGSTRate: newRow.IGSTRate,
      GSTRate: newRow.GSTRate,
      HSNSAC: newRow.HSNSAC,
      TaxCategory: newRow.TaxCategory,
      IsSystemGenerated: false,
    });

    refetch();
    toast.success('Data updated');
    return updatedRow;
  };

  const onPaginationModelChange = (model: GridPaginationModel) => {
    setPage(model.page);
    setRowsPerPage(model.pageSize);
  };

  return (
    <div className="mt-4">
      <Typography variant="h4" sx={{ color: 'black', fontWeight: '600' }}>
        Stock Master
      </Typography>
      <div className="flex py-4 items-center justify-between">
        <div className="font-semibold text-xl">Organization: {tenant.name}</div>
        <FileUpload
          onFileUpload={onFileUpload}
          headers={expectedStockMasterHeaders}
          addComponentType={AddComponentType.StockMaster}
        />
      </div>
      <div className="my-2">{isProcessing && <LinearProgress />}</div>
      <DataGrid
        rows={stockMasterData ?? []}
        columns={Columns(
          rowModesModel,
          setRowModesModel,
          tenant.id,
          handleManageMappings,
        )}
        rowModesModel={rowModesModel}
        onRowModesModelChange={handleRowModesModelChange}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        editMode="row"
        slots={{ toolbar: CustomToolbar }}
        disableColumnSelector
        disableDensitySelector
        autoHeight
        slotProps={{
          toolbar: {
            showQuickFilter: true,
          },
        }}
        sx={{
          boxShadow: 2,
          borderColor: 'blueGrey',
          '& .MuiDataGrid-cell:hover': {
            color: 'primary.light',
          },
          '& .gridHeader--header': {
            color: '#000000',
            fontSize: 'large',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: '700',
            color: '#020617',
          },
          '.row-red': {
            background: '#ff9100',
            color: '#000000',
          },
        }}
        loading={isLoading}
        classes={customClasses}
        pageSizeOptions={[5, 10, 20]}
        paginationModel={{ page, pageSize: rowsPerPage }}
        onPaginationModelChange={onPaginationModelChange}
        getRowClassName={params =>
          params.row.IsSystemGenerated ? 'row-red' : ''
        }
      />
      <Snackbar
        open={showErrorAlert}
        autoHideDuration={3000}
        onClose={(_event, reason) => handleClose(reason)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="error"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION FAILED:</AlertTitle>
          FILE FORMAT DID NOT MATCH
        </Alert>
      </Snackbar>
      <Snackbar
        open={showSuccessAlert}
        autoHideDuration={1500}
        onClose={() => setShowSuccessAlert(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert
          variant="filled"
          onClose={() => setShowErrorAlert(false)}
          severity="success"
          sx={{ width: '100%' }}>
          <AlertTitle>VALIDATION PASSED:</AlertTitle>
          UPLOADING FILE FOR PROCESSING.
        </Alert>
      </Snackbar>

      {/* Stock Mappings Dialog */}
      {selectedStock && (
        <StockMappingsDialog
          open={mappingsDialogOpen}
          onClose={handleCloseMappingsDialog}
          stockId={selectedStock.id}
          stockName={selectedStock.name}
          stockOCR={selectedStock.stockOCR}
          partitionKey={selectedStock.partitionKey}
          matchingStockAliases={selectedStock.matchingStockAliases}
          onMappingsChanged={refetch}
        />
      )}
    </div>
  );
};
export default StockMaster;
